<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Lenis from '@studio-freight/lenis'
import Header from '../components/HeaderView.vue'
import Footer from '../components/FooterView.vue'
import Breadcrumbs from '../components/Breadcrumbs.vue'
import RelatedLinks from '../components/RelatedLinks.vue'
import { blogApi } from '../lib/supabase'
import { useSEO } from '../composables/useSEO'
import { generateHTML } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import Image from '@tiptap/extension-image'

const route = useRoute()
const router = useRouter()
const publicidadImage = new URL('../assets/images/publicidad.png', import.meta.url).href
const { updateMetaTags, resetMetaTags } = useSEO()

let lenis: Lenis | null = null

// --- INICIO DE CAMBIOS ---

// 1. Estado reactivo para el post actual
const currentPost = ref<{
  id: string
  title: string
  slug: string
  cover_image_url: string | null
  published_at: string | null
  created_at: string
  author_name: string | null
  content_sections: Array<{
    id: string
    order: number
    type: string
    content: {
      text?: string
      url?: string
      alt?: string
      items?: string[]
    }
  }>
  post_tags: Array<{ name: string }>
} | null>(null)
const isLoading = ref(true)
const errorMsg = ref<string | null>(null)
const readingTime = ref<number>(5) // Tiempo de lectura en minutos

// 3. Función para cargar el post basado en el slug de la URL
async function fetchPost(slug: string) {
  if (!slug) return
  try {
    isLoading.value = true
    const data = await blogApi.getPostBySlug(slug)

    if (!data) {
      // Si el post no se encuentra, redirigir al blog
      router.push('/blog')
      return
    }

    currentPost.value = data

    // Calcular tiempo de lectura (promedio 200 palabras por minuto)
    if (data.content_sections) {
      const totalWords = data.content_sections
        .filter((section: any) => section.type === 'PARAGRAPH')
        .reduce((acc: number, section: any) => {
          const text = section.content?.text || ''
          return acc + text.split(/\s+/).length
        }, 0)
      readingTime.value = Math.max(1, Math.ceil(totalWords / 200))
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
    errorMsg.value = `Error al cargar el post: ${errorMessage}`
    console.error(error)
  } finally {
    isLoading.value = false
  }
}

// 4. Observar cambios en la ruta (para cuando navegas entre posts relacionados)
watch(
  () => route.params.slug,
  (newSlug) => {
    fetchPost(newSlug as string)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  },
  { immediate: true }, // Ejecutar inmediatamente al cargar el componente
)

// 5. Observar cambios en el post para actualizar SEO
watch(
  currentPost,
  (newPost) => {
    if (newPost) {
      // Extraer el primer párrafo para la descripción
      const firstParagraph =
        newPost.content_sections?.find((section) => section.type === 'PARAGRAPH')?.content.text ||
        newPost.title

      const description =
        firstParagraph.length > 155 ? firstParagraph.substring(0, 155) + '...' : firstParagraph

      // Extraer keywords de los tags
      const keywords = newPost.post_tags?.map((tag: any) => tag.name).join(', ') || undefined

      updateMetaTags({
        title: newPost.title,
        description: description,
        keywords: keywords,
        image: newPost.cover_image_url || undefined,
        url: `https://fourin.co/blog/${newPost.slug}`,
        canonical: `https://fourin.co/blog/${newPost.slug}`,
        type: 'article',
        publishedTime: newPost.published_at || newPost.created_at,
        modifiedTime: newPost.created_at,
        author: newPost.author_name || undefined,
        breadcrumbs: [
          { name: 'Inicio', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: newPost.title, url: `/blog/${newPost.slug}` }
        ]
      })
    } else {
      resetMetaTags()
    }
  },
  { immediate: true },
)

// Función para detectar si es contenido legacy
function isLegacyContent(content: any): boolean {
  return (
    typeof content === 'object' &&
    'text' in content &&
    !('type' in content || 'content' in content)
  )
}

// Función para renderizar contenido ProseMirror
function renderProseMirrorContent(content: any): string {
  if (!content) return ''
  return generateHTML(content, [
    StarterKit,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Image
  ])
}

// --- FIN DE CAMBIOS ---

onMounted(() => {
  lenis = new Lenis({
    lerp: 0.1,
    smoothWheel: true,
  })

  function raf(time: number) {
    lenis?.raf(time)
    requestAnimationFrame(raf)
  }

  requestAnimationFrame(raf)
})

onBeforeUnmount(() => {
  if (lenis) {
    lenis.destroy()
  }
})
</script>

<template>
  <Header />
  <main>

    <!-- Skeleton Loader -->
    <div v-if="isLoading" class="skeleton-container">
      <div class="container">
        <!-- Hero Section Skeleton -->
        <section class="post-hero-skeleton">
          <div class="breadcrumbs-skeleton">
            <div class="skeleton-breadcrumb"></div>
            <div class="skeleton-separator"></div>
            <div class="skeleton-breadcrumb"></div>
            <div class="skeleton-separator"></div>
            <div class="skeleton-breadcrumb"></div>
          </div>

          <div class="hero-content-skeleton">
            <div class="skeleton-post-date"></div>
            <div class="skeleton-post-title"></div>
            <div class="skeleton-featured-image"></div>

            <div class="author-info-skeleton">
              <div class="skeleton-author-avatar"></div>
              <div class="skeleton-author-details">
                <div class="skeleton-author-name"></div>
                <div class="skeleton-read-time"></div>
              </div>
            </div>
          </div>
        </section>

        <!-- Article Content Skeleton -->
        <section class="article-content-skeleton">
          <div class="content-wrapper-skeleton">
            <article class="main-content-skeleton">
              <div class="content-skeleton">
                <div class="skeleton-paragraph"></div>
                <div class="skeleton-heading"></div>
                <div class="skeleton-paragraph"></div>
                <div class="skeleton-paragraph"></div>
                <div class="skeleton-image"></div>
                <div class="skeleton-paragraph"></div>
                <div class="skeleton-heading"></div>
                <div class="skeleton-paragraph"></div>
                <div class="skeleton-paragraph"></div>
              </div>

              <div class="article-tags-skeleton">
                <div class="skeleton-tags-title"></div>
                <div class="skeleton-tags">
                  <div class="skeleton-tag"></div>
                  <div class="skeleton-tag"></div>
                  <div class="skeleton-tag"></div>
                </div>
              </div>
            </article>

            <aside class="sidebar-skeleton">
              <div class="sidebar-section-skeleton">
                <div class="skeleton-sidebar-title"></div>
                <div class="skeleton-sidebar-content"></div>
              </div>
            </aside>
          </div>
        </section>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMsg" class="container">
      <div style="text-align: center; padding: 2rem; color: red">
        <p>{{ errorMsg }}</p>
      </div>
    </div>

    <div v-if="currentPost && !isLoading">
      <!-- Hero Section -->
      <section class="post-hero">
        <div class="container">
          <Breadcrumbs
            v-if="currentPost"
            :items="[
              { name: 'Inicio', url: '/' },
              { name: 'Blog', url: '/blog' },
              { name: currentPost.title, url: `/blog/${currentPost.slug}` }
            ]"
          />

          <div class="hero-content">
            <div class="post-meta">
              <span class="post-date">{{
                new Date(currentPost.published_at || currentPost.created_at).toLocaleDateString(
                  'es-ES',
                  { year: 'numeric', month: 'long', day: 'numeric' },
                )
              }}</span>
            </div>

            <h1 class="post-title">{{ currentPost.title }}</h1>
            <section class="">
              <img
                :src="currentPost.cover_image_url || '/placeholder-image.jpg'"
                :alt="currentPost.title"
                class="featured-image"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            </section>

            <div class="author-info">
              <div class="author-avatar">
                <span>{{ (currentPost.author_name || 'Autor').charAt(0) }}</span>
              </div>
              <div class="author-details">
                <span class="author-name">{{
                  currentPost.author_name || 'Autor Desconocido'
                }}</span>
                <span class="read-time">{{ readingTime }} min lectura</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Article Content -->
      <section class="article-content">
        <div class="container">
          <div class="content-wrapper">
            <article class="main-content">
              <!-- Renderizar el contenido dinámico desde 'content_sections' -->
              <div class="content">
                <div
                  v-for="section in currentPost.content_sections.sort(
                    (a: any, b: any) => a.order - b.order,
                  )"
                  :key="section.id"
                >
                  <h2 v-if="section.type === 'HEADING'" class="content-heading" :id="`heading-${section.id}`">
                    {{ section.content.text }}
                  </h2>

                  <!-- Sección de párrafo actualizada con renderizado condicional -->
                  <template v-else-if="section.type === 'PARAGRAPH'">
                    <div v-if="isLegacyContent(section.content)" class="content-paragraph">
                      {{ section.content.text }}
                    </div>
                    <div
                      v-else
                      class="content-paragraph prosemirror-content"
                      v-html="renderProseMirrorContent(section.content)"
                    ></div>
                  </template>

                  <!-- Image sections remain unchanged -->
                  <img
                    v-else-if="section.type === 'IMAGE'"
                    :src="section.content.url"
                    :alt="section.content.alt"
                    class="content-image"
                  />

                  <!-- List sections remain unchanged -->
                  <ul v-else-if="section.type === 'LIST'" class="content-list">
                    <li v-for="(item, index) in section.content.items" :key="index">
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </div>

              <div class="article-tags">
                <h3>Etiquetas:</h3>
                <div class="tags">
                  <!-- CAMBIO: Accedemos directamente a postTag.name -->
                  <span v-for="postTag in currentPost.post_tags" :key="postTag.name" class="tag">
                    {{ postTag.name }}
                  </span>
                </div>
              </div>

              <!-- Related Links Section -->
              <RelatedLinks
                v-if="currentPost"
                :links="[
                  {
                    title: 'Software CMMS en Colombia: Guía Completa 2025',
                    url: '/cmms-colombia',
                    description: 'Comparativa completa de software CMMS',
                    type: 'page'
                  },
                  {
                    title: 'GMAO: Qué es, beneficios y cómo elegir en LATAM',
                    url: '/gmao-latinoamerica',
                    description: 'Guía completa sobre GMAO en Latinoamérica',
                    type: 'page'
                  },
                  {
                    title: 'Fourintrack - Software CMMS/GMAO',
                    url: '/products',
                    description: 'Conoce nuestro software CMMS en la nube',
                    type: 'product'
                  }
                ]"
                title="Contenido Relacionado"
              />
            </article>

            <aside class="sidebar">
              <div class="sidebar-section">
                <div class="author-card">
                  <div class="author-avatar-large">
                    <span>{{ (currentPost.author_name || 'Autor').charAt(0) }}</span>
                  </div>
                  <h4>{{ currentPost.author_name || 'Autor Desconocido' }}</h4>
                  <p>Experto en tecnología</p>
                </div>
              </div>

              <!-- Publicidad ftrack -->
              <div class="sidebar-section">
                <a href="https://app.fourin.co" target="_blank">
                  <img
                    :src="publicidadImage"
                    class="advertisement-image"
                    alt="ftrack Software"
                    width="360"
                  />
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>

    <Footer />
  </main>
</template>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.post-hero {
  padding: 80px 0 60px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #64748b;
  margin-bottom: 40px;
}

.breadcrumbs a {
  color: #1297ef;
  text-decoration: none;
  transition: color 0.3s ease;
}

.breadcrumbs a:hover {
  color: #0d5fef;
}

.separator {
  color: #cbd5e1;
}

.post-meta {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.post-category {
  padding: 6px 12px;
  background: #1297ef;
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.post-date {
  color: #64748b;
  font-size: 14px;
}

.post-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
  line-height: 1.2;
}

.post-excerpt {
  font-size: 1.25rem;
  color: #64748b;
  margin: 30px 0;
  line-height: 1.6;
  max-width: 800px;
}

.author-info {
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 20px 0px 0px 0px;
}

.author-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
}

.author-details {
  display: flex;
  flex-direction: column;
}

.author-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 16px;
}

.read-time {
  color: #64748b;
  font-size: 14px;
}

/* Featured Image */
.featured-image-section {
  padding: 10px 0;
}

.featured-image {
  border-radius: 12px;
  width: 100%;
  height: 400px;
  object-fit: cover;
  object-position: center;
}

.featured-image img {
  width: 100%;
  height: 400px;
  object-fit: cover;
}

/* Article Content */
.article-content {
  padding: 60px 0;
  background: white;
}

.content-wrapper {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 60px;
  align-items: start;
}

.main-content {
  max-width: 800px;
}

.content {
  font-size: 1.125rem;
  line-height: 1.8;
  color: #374151;
}

.content h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1e293b;
  margin: 60px 0 30px;
}

.content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 40px 0 20px;
}

.content p {
  margin-bottom: 30px;
}

.content ul {
  margin: 30px 0;
  padding-left: 20px;
}

.content li {
  margin-bottom: 15px;
}

.content strong {
  font-weight: 600;
  color: #1e293b;
}

.content-image {
  width: -webkit-fill-available;
  border-radius: 12px;
  margin: 20px 0;
}

.parrafo {
  margin: 0px 0px 40px 0px;
}

.article-tags {
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #e5e7eb;
}

.article-tags h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag {
  padding: 8px 16px;
  background: #f3f4f6;
  color: #374151;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.tag:hover {
  background: #1297ef;
  color: white;
}

/* Sidebar */
.sidebar {
  position: sticky;
  top: 100px;
}

.sidebar-section {
  margin-bottom: 40px;
}

.author-card {
  background: #f8fafc;
  padding: 25px;
  border-radius: 12px;
  text-align: center;
}

.author-avatar-large {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(45deg, #1297ef, #4ab7ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 24px;
  margin: 0 auto 15px;
}

.author-card h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.author-card p {
  color: #64748b;
  font-size: 14px;
}

.advertisement-card {
  background: #f8fafc;
  border-radius: 12px;
  text-align: center;
  margin-top: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.advertisement-card:hover {
  border-color: #1297ef;
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(18, 151, 239, 0.15);
}

.advertisement-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
}

.advertisement-link:hover {
  color: #1297ef;
}

.advertisement-image {
  width: 360px;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.advertisement-image:hover {
  transform: scale(1.05);
}

.advertisement-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.advertisement-content h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
  transition: color 0.3s ease;
}

.advertisement-content p {
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
}

.related-posts {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.related-post {
  display: flex;
  gap: 15px;
  padding: 15px;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.related-post:hover {
  background: #f8fafc;
  transform: translateX(5px);
}

.related-post-image {
  width: 80px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.related-post-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.related-post-content h4 {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 5px;
  line-height: 1.4;
}

.related-post-date {
  font-size: 12px;
  color: #64748b;
}

/* Newsletter Section */
.newsletter {
  padding: 80px 0;
  background: linear-gradient(135deg, #1297ef 0%, #4ab7ff 100%);
  color: white;
}

.newsletter-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.newsletter-content h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
}

.newsletter-content p {
  font-size: 1.125rem;
  margin-bottom: 40px;
  opacity: 0.9;
}

.newsletter-form {
  display: flex;
  gap: 12px;
  max-width: 400px;
  margin: 0 auto;
}

.newsletter-input {
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
}

.newsletter-btn {
  padding: 12px 24px;
  background: white;
  color: #1297ef;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.newsletter-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 255, 255, 0.3);
}

/* ===== SKELETON LOADER STYLES ===== */

.skeleton-container {
  padding: 40px 0;
}

/* Hero Section Skeleton */
.post-hero-skeleton {
  padding: 60px 0 40px;
}

.breadcrumbs-skeleton {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 40px;
}

.skeleton-breadcrumb {
  width: 60px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-separator {
  width: 8px;
  height: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 50%;
}

.hero-content-skeleton {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.skeleton-post-date {
  width: 150px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-post-title {
  width: 100%;
  height: 48px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-featured-image {
  width: 100%;
  height: 400px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

.author-info-skeleton {
  display: flex;
  align-items: center;
  gap: 15px;
}

.skeleton-author-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-author-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-author-name {
  width: 120px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-read-time {
  width: 60px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

/* Article Content Skeleton */
.article-content-skeleton {
  padding: 60px 0;
}

.content-wrapper-skeleton {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 60px;
  max-width: 1200px;
  margin: 0 auto;
}

.main-content-skeleton {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.content-skeleton {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.skeleton-paragraph {
  width: 100%;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-paragraph:nth-child(2) {
  width: 95%;
}

.skeleton-paragraph:nth-child(3) {
  width: 90%;
}

.skeleton-heading {
  width: 70%;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-image {
  width: 100%;
  height: 300px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.article-tags-skeleton {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.skeleton-tags-title {
  width: 100px;
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-tags {
  display: flex;
  gap: 10px;
}

.skeleton-tag {
  width: 80px;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 16px;
}

/* Sidebar Skeleton */
.sidebar-skeleton {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.sidebar-section-skeleton {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.skeleton-sidebar-title {
  width: 150px;
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 20px;
}

.skeleton-sidebar-content {
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

/* Shimmer Animation */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .post-title {
    font-size: 2rem;
  }

  .content-wrapper {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .sidebar {
    position: static;
  }

  .post-meta {
    flex-direction: column;
    gap: 10px;
  }

  .newsletter-form {
    flex-direction: column;
  }

  .newsletter-content h2 {
    font-size: 2rem;
  }

  /* Skeleton Responsive */
  .content-wrapper-skeleton {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .skeleton-featured-image {
    height: 300px;
  }

  .skeleton-post-title {
    height: 36px;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 15px;
  }

  .post-hero {
    padding: 60px 0 40px;
  }

  .post-title {
    font-size: 1.75rem;
  }

  .featured-image img {
    height: 250px;
  }

  .article-content {
    padding: 40px 0;
  }
}
</style>
